import React, { memo, useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import Cookies from 'js-cookie';

import { BugSnagWrapper } from './BugSnagWrapper';
import UtilFormat from 'utils/UtilFormat';
import { intercomMutationObserver } from 'utils/intercom';
import { userflowMutationObserver } from 'utils/userflow';

import { goLogout, goLogin, isS200ukiOnboardingRoute } from 'utils/routes';
import metadata from 'lib/metadata';

import { getTokenFromOauth } from 'services/Auth';
import { login } from 'actions/Config';

import TabLoader from 'containers/TabLoader';
import MainContainer from 'containers/Main';
import { EXTERNAL_ROUTES } from './routes';

const isNoSessionRoute = () => {
    return !!EXTERNAL_ROUTES.find(
        (route) => window.location.hash.includes(route.path) && route.noRedirectLogin,
    );
};

const MainApp = ({ store, context }) => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        function initAsync() {
            context
                .initAsync()
                .then(() => {
                    UtilFormat.setConfig(context.cacheManager.getConfigStore());
                })
                .catch((e) => {
                    console.error(e);
                    goLogout();
                })
                .finally(() => setLoading(false));
        }

        // Checking oauth and auto-auth params
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);

        // OAUTH — skip on S200UKI onboarding; SageID `code`/`state` stay for container bootstrap.
        // Session skip for this route still uses isNoSessionRoute() + EXTERNAL_ROUTES / EXTERNAL_ROUTES_HASH.
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        if (state && code && !isS200ukiOnboardingRoute()) {
            const oauthUrl = window.location.href;
            const uri = window.location.toString();
            const clean_uri = uri.substring(0, uri.indexOf('?'));
            window.history.replaceState({}, document.title, clean_uri);
            getTokenFromOauth(state, oauthUrl)
                .then((result) => {
                    const email = result.Message;
                    const token = result.Result;
                    return login(email, token, token)();
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    setLoading(false);
                });
            return;
        }

        // If AUTO AUTH skip current init
        if (urlParams.has('user') && urlParams.has('token')) {
            setLoading(false);
            return;
        }

        // If LOGOUT skip current init too
        if (window.location.hash.includes('logout')) {
            setLoading(false);
            return;
        }

        let currentConfig = context.cacheManager.getConfigStore();
        if (!currentConfig) {
            if (Cookies.get('token')) return initAsync();
            else {
                setLoading(false);
                if (isNoSessionRoute()) return;
                goLogin();
            }
        } else {
            initAsync();
        }
    }, [context]);

    intercomMutationObserver();
    userflowMutationObserver();

    return (
        <BugSnagWrapper>
            <HelmetProvider>
                <Provider store={store.store}>
                    <PersistGate loading={null} persistor={store.persistor}>
                        <Helmet>
                            <title>{metadata.title}</title>
                            {/* file deepcode ignore DOMXSS: controlled url */}
                            <link rel="icon" href={metadata.favicon}></link>
                        </Helmet>
                        {!loading && <MainContainer context={context} />}
                        {loading && <TabLoader />}
                    </PersistGate>
                </Provider>
            </HelmetProvider>
        </BugSnagWrapper>
    );
};

export default memo(MainApp);
